import { useState } from "react";
import { Star, X, Send } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

/** Reusable star input (1-5) */
function StarPicker({ value, onChange, size = "w-8 h-8" }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`star-${n}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          className="p-0.5 transition-transform hover:scale-110"
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
        >
          <Star
            className={`${size} ${
              (hover || value) >= n ? "text-[#FBBF24] fill-[#FBBF24]" : "text-zinc-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReviewSubmitModal({ scope = "app", targetId = null, onClose, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error("Pick a rating from 1 to 5 stars.");
      return;
    }
    setSubmitting(true);
    try {
      const body = { scope, target_id: targetId, rating, text, name, email };
      const res = await fetch(`${API}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to submit");
      toast.success("Thanks!");
      onSubmitted?.(data);
      onClose();
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="review-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-4 border-black shadow-[8px_8px_0_0_#000] max-w-lg w-full max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-6 py-4 bg-black text-white">
          <p className="font-mono text-xs uppercase tracking-widest">
            {scope === "note" ? "Rate this note" : "Rate Smart S Notes"}
          </p>
          <button
            data-testid="review-close"
            onClick={onClose}
            className="p-1 hover:bg-white/10"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
              Your rating
            </label>
            <StarPicker value={rating} onChange={setRating} />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
              What did you think? <span className="text-zinc-400 lowercase tracking-normal font-sans">(optional, 800 chars max)</span>
            </label>
            <textarea
              data-testid="review-text"
              rows={4}
              maxLength={800}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Tell others how Smart S Notes helped you…"
              className="w-full px-3 py-2 bg-white border-2 border-black focus:outline-none focus:ring-2 focus:ring-[#3B82F6] resize-y"
            />
            <p className="mt-1 font-mono text-[10px] text-zinc-400">{text.length}/800</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Name</label>
              <input
                data-testid="review-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full px-3 py-2 bg-white border-2 border-black focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Email</label>
              <input
                data-testid="review-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2 bg-white border-2 border-black focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
              />
            </div>
          </div>

          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Your review helps other students choose Smart Notes.
          </p>

          <button
            data-testid="review-submit"
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-widest px-6 py-3 bg-[#3B82F6] text-white btn-brutal disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Submit review"} <Send className="w-3.5 h-3.5" />
          </button>
        </form>
      </div>
    </div>
  );
}
